import { Just, type Maybe, Nothing } from "purify-ts";
import { match } from "ts-pattern";
import type { GetArrayElementType } from "widgets/utils";

import type { ActivityInstance, ActivityInstanceParameter, WorkflowConfig } from "@/graphql.ts";
import type {
  BooleanParameterValueFragment,
  FileParameterValueFragment,
  GenomeParameterValueFragment,
  GetActivitiesQuery,
  NumberParameterValueFragment,
  RegionParameterValueFragment,
  SampleParameterValueFragment,
  StringParameterValueFragment,
  TableParamValueFragment,
  TrackerParameterValueFragment,
} from "@/modules/workflow/graphql/workflow.gql.ts";
import { getSampleFileMappings } from "@/modules/workflow/utils/sampleFileMappings.ts";
import type { Activity, AllParametersTypes, RegionParamType } from "@/modules/workflow/utils/types.ts";

export const extractGetActivitiesData = (data: GetActivitiesQuery | null | undefined) => {
  const activities = data?.workflow?.activityCollector.activities;
  const errors = data?.workflow?.activityCollector.errors;

  return { activities, errors };
};

/**
 * Find the latest version from a list of versions.
 */
export const findLatestVersion = (versions: string[]): string | null => {
  if (versions.length === 0) {
    return null;
  }
  return versions.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })).reverse()[0];
};

/**
 * If workflowActivityParam is given the function returns a Just of this value.
 * If no param is given but withDefault = true the activity default value is provided as a Just.
 * If both conditions do not hold the function returns a Nothing
 *
 * @param parameter
 * @param workflowActivityParam
 * @param withDefault
 */
export async function getValueFromWorkflowParameter(
  parameter: GetArrayElementType<NonNullable<Activity["parameters"]>>,
  workflowActivityParam?: ActivityInstanceParameter,
  withDefault = true,
): Promise<Maybe<AllParametersTypes>> {
  return await match(parameter)
    .with({ __typename: "StringParam" }, ({ stringDefaultValue: defaultValue }) => {
      const value = workflowActivityParam
        ? (workflowActivityParam.values as StringParameterValueFragment)?.stringValue
        : withDefault
          ? defaultValue
          : null;

      return value ? Just(value) : Nothing;
    })
    .with({ __typename: "NumberParam" }, ({ numberDefaultValue: defaultValue }) => {
      const value = workflowActivityParam
        ? (workflowActivityParam.values as NumberParameterValueFragment)?.numberValue
        : withDefault
          ? defaultValue
          : null;
      return value ? Just(value) : Nothing;
    })
    .with({ __typename: "BooleanParam" }, ({ booleanDefaultValue: defaultValue }) => {
      const value = workflowActivityParam
        ? (workflowActivityParam.values as BooleanParameterValueFragment)?.booleanValue
        : withDefault
          ? defaultValue
          : null;

      return Just(value ?? false);
    })
    .with({ __typename: "GenomeParam" }, () => {
      /* No default value for Genome */
      const genomeValue = (workflowActivityParam?.values as GenomeParameterValueFragment)?.genomeValue;
      const value = genomeValue?.filter((v) => v != null).map((v) => (v.hidden ? -1 : v.id)); // make sure inaccessible resources have an invalid id

      return value ? Just(value) : Nothing;
    })
    .with({ __typename: "FileParam" }, () => {
      /* No default value for File */

      // Remove null values (wrong fileId not in the DB)
      const value = (workflowActivityParam?.values as FileParameterValueFragment)?.fileValue
        .map((val) => (val?.hidden ? -1 : val?.deleted ? -2 : val?.id)) // make sure inaccessible resources have an invalid id
        .filter<number>((val): val is number => typeof val === "number");

      return value?.length ? Just(value) : Nothing;
    })
    .with({ __typename: "SampleParam" }, async () => {
      /* No default value for File */
      const values = workflowActivityParam?.values as SampleParameterValueFragment;

      if (values?.sampleValue && values?.attribute?.id != undefined) {
        const maybeValue = {
          attributeId: values.attribute.id,
          // The file association may have changed. So we reload the files!
          sampleFileMappings: await getSampleFileMappings(
            values.attribute.id,
            values.sampleValue
              .filter((value) => value != undefined)
              .map((value) => (value?.sample?.hidden ? -1 : value?.sample?.deleted ? -2 : value!.sample?.id)),
          ),
        };

        return Just(maybeValue);
      }

      return Nothing;
    })
    .with({ __typename: "RegionParam" }, () => {
      /* No default value for Region */

      // Parse value
      let value: RegionParamType | null = null;
      const regionValue = (workflowActivityParam?.values as RegionParameterValueFragment)?.regionValue;
      if (regionValue?.contig) {
        value = { contigId: regionValue.contig.id, range: regionValue.range };
      }

      return value ? Just(value) : Nothing;
    })
    .with({ __typename: "DateParam" }, () => {
      /* DO NOTHING */
      return Nothing;
    })
    .with({ __typename: "TrackerParam" }, () => {
      /* No default value for Region */
      const value = (workflowActivityParam?.values as TrackerParameterValueFragment)?.trackerValue?.id;

      return value ? Just(value) : Nothing;
    })
    .with({ __typename: "TableParam" }, () => {
      const value = (workflowActivityParam?.values as TableParamValueFragment)?.tableValue;

      return value?.id ? Just(value.id) : Nothing;
    })
    .exhaustive();
}

// used for topological sorting
function DepthFirstSearch(
  node: ActivityInstance,
  visited: Set<string>,
  stack: ActivityInstance[],
  workflowConfig: WorkflowConfig,
): void {
  visited.add(node.identifier);

  // Filtering for child connections where the current node is the source
  const childConnections = workflowConfig.connectors.filter((connector) => connector.source === node.identifier);
  for (const connection of childConnections) {
    const childActivity = workflowConfig.activities.find((activity) => activity.identifier === connection.target);
    if (childActivity && !visited.has(childActivity.identifier)) {
      DepthFirstSearch(childActivity, visited, stack, workflowConfig);
    }
  }

  // Push the current node onto the stack after all dependents are handled
  stack.push(node);
}

export function topologicalSortOfActivities(workflowConfig: WorkflowConfig): ActivityInstance[] {
  const stack: ActivityInstance[] = [];
  const visited = new Set<string>();

  for (const activityInstance of workflowConfig.activities) {
    if (!visited.has(activityInstance.identifier)) {
      DepthFirstSearch(activityInstance, visited, stack, workflowConfig);
    }
  }

  // Return the stack reversed to get the correct topological order
  return stack.reverse();
}
